import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import DatePicker, { registerLocale } from "react-datepicker";
import { pt } from "date-fns/locale/pt";
import { Calendar, ChevronDown, Download } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { analyticsApi } from "../../services/analyticsApi";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import { saveBlob } from "../../utils/csvFile";
import { MIN_DATE, PRESETS, TODAY, lastPickableDay, previousPeriod, timeIn, todayIn } from "../../utils/analyticsPeriod";
import { formatDay } from "../../utils/analyticsLabels";
import { dayKey } from "../../utils/datetime";
import InfoTip from "../charts/InfoTip";
import usePopover from "./usePopover";
import { LOCALES } from "../charts/format";
import "react-datepicker/dist/react-datepicker.css";

// O calendario fala a lingua do backoffice, como o das conversas.
registerLocale("pt", pt);

// Com `responseType: "blob"` o corpo de um erro tambem chega como Blob, e o `detail` do FastAPI esta
// dentro dele: le-se o texto e passa-se ao apiErrorMessage como um corpo JSON normal. Um corpo que
// nao seja JSON (a pagina de erro de um proxy) fica sem `detail`, e sai a frase generica.
const withReadableBody = async (err) => {
  const body = err.response?.data;
  if (!(body instanceof Blob)) return err;
  const text = await body.text();
  try {
    return { response: { data: JSON.parse(text) } };
  } catch {
    return err;
  }
};

const control = (pressed) => ({
  borderColor: pressed ? "var(--color-mint)" : "var(--color-border)",
  backgroundColor: pressed ? "var(--color-mint-muted)" : "var(--color-surface)",
  color: "var(--color-text-primary)",
});

// AAAA-MM-DD como a meia-noite local, que e o que o react-datepicker compara.
const toDate = (isoDate) => new Date(`${isoDate}T00:00:00`);

// As tres exportacoes do menu (entrega 5 do redesign): o pedido e o nome do ficheiro, o mesmo do
// Content-Disposition do backend. Quem ve cada uma decide a pagina (`exportOptions`).
const EXPORTS = {
  conversations: { request: (chatId, period) => analyticsApi.conversationsCsv(chatId, period), file: "conversations" },
  daily: { request: (chatId, period) => analyticsApi.dailyCsv(chatId, period), file: "analytics" },
  firstQuestions: { request: (chatId, period) => analyticsApi.firstQuestionsCsv(chatId, period), file: "content-and-assistant" },
};

// O menu Exportar: uma lista de botoes por baixo do botao, e nao um role="menu", que pedia setas; o Tab
// chega as opcoes. Fecha com um clique fora, com o foco a chegar a qualquer sitio fora dele e com o Escape,
// que so devolve o foco ao botao quando ele estava dentro, como o calendario (ver `usePopover`).
// No telemovel o botao fica so com o icone (spec do redesign, R9), com o nome para o leitor de ecra. Cada
// opcao e um pedido a parte: contra um backend sem a rota dela, o toast diz que falhou e a pagina fica.
const ExportMenu = ({ chatId, period, options }) => {
  const { translate } = useLanguage();
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const listId = useId();

  usePopover(menuRef, { open, onClose: () => setOpen(false), returnFocusTo: buttonRef });

  // Escolher uma opcao fecha a lista, e com ela a opcao que tinha o foco. O foco passa ja ao botao, que
  // durante o pedido fica `aria-disabled` e `aria-busy` em vez de `disabled`: um botao disabled perde o
  // foco, que cairia no body ate ao fim do pedido (1 a 3 segundos), com o leitor de ecra a nao dizer nada.
  const download = async (option) => {
    setOpen(false);
    setExporting(true);
    buttonRef.current.focus();
    try {
      saveBlob(await EXPORTS[option].request(chatId, period), `${EXPORTS[option].file}-${period.from}-${period.to}.csv`);
    } catch (err) {
      // Uma 404 e a rota que o backend ainda nao tem: o "Not Found" do FastAPI e em ingles, por isso fica
      // a frase da opcao. Os outros erros mostram o `detail` do backend, quando o houver.
      const failed = translate(`analyticsExportFailed.${option}`);
      toast.error(err.response?.status === 404 ? failed : apiErrorMessage(await withReadableBody(err), failed));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={menuRef} className="relative ml-auto">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={translate("analyticsExport")}
        aria-busy={exporting}
        aria-disabled={exporting}
        onClick={() => !exporting && setOpen((current) => !current)}
        disabled={!chatId}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border disabled:opacity-50 ${exporting ? "opacity-50" : ""}`}
        style={control(false)}
      >
        <Download className="w-4 h-4" aria-hidden="true" />
        <span className="hidden sm:inline">{translate("analyticsExport")}</span>
        <ChevronDown className="hidden sm:inline w-4 h-4" aria-hidden="true" />
      </button>
      {open && (
        <ul
          id={listId}
          aria-label={translate("analyticsExport")}
          className="absolute right-0 top-full mt-2 z-30 w-60 max-w-[calc(100vw-2rem)] rounded-lg border py-1"
          style={{ backgroundColor: "var(--color-surface)", borderColor: "var(--color-border)", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
        >
          {options.map((option) => (
            <li key={option}>
              <button
                type="button"
                onClick={() => download(option)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-surface-raised)]"
                style={{ color: "var(--color-text-primary)" }}
              >
                {translate(`analyticsExportOption.${option}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// O periodo a escolha, no calendario do filtro das conversas: o botao diz sempre as datas do periodo
// ativo, e e premido quando elas foram escolhidas a mao. O periodo da pagina so muda com os dois dias;
// o primeiro fica aqui a espera do segundo, e fechar o calendario larga-o.
const CustomPeriod = ({ period, custom, onRange }) => {
  const { translate, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [pickedStart, setPickedStart] = useState(null);
  const [alignRight, setAlignRight] = useState(false);
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const calendarRef = useRef(null);

  // O calendario abre alinhado a esquerda do botao. Se assim passasse da margem direita do <main>,
  // que rola na horizontal e o cortava, alinha-se a direita do botao: com o botao no inicio de uma
  // linha (ecras estreitos) cabe a esquerda, e com ele no fim da linha cabe a direita.
  useLayoutEffect(() => {
    if (!open) return;
    const calendar = calendarRef.current.getBoundingClientRect();
    const limit = calendarRef.current.closest("main")?.getBoundingClientRect().right ?? document.documentElement.clientWidth;
    if (calendar.right > limit) setAlignRight(true);
  }, [open]);

  // Fecha com um clique fora, com o foco a chegar a qualquer sitio fora do calendario e com o Escape, que so
  // devolve o foco ao botao quando ele estava dentro, como o painel das conversas (ver `usePopover`).
  const close = () => {
    setOpen(false);
    setPickedStart(null);
  };
  usePopover(pickerRef, { open, onClose: close, returnFocusTo: buttonRef });

  const toggle = () => {
    setOpen((current) => !current);
    setPickedStart(null);
    setAlignRight(false);
  };

  // O calendario devolve [inicio, fim], com o fim a null ate ao segundo clique.
  const choose = ([start, end]) => {
    if (!end) {
      setPickedStart(start);
      return;
    }
    onRange({ preset: null, custom: { from: dayKey(start), to: dayKey(end) } });
    setOpen(false);
    setPickedStart(null);
  };

  const dates =
    period.from === period.to
      ? formatDay(period.from, language)
      : translate("analyticsPeriodRange", { from: formatDay(period.from, language), to: formatDay(period.to, language) });
  const lastDay = lastPickableDay(pickedStart && dayKey(pickedStart), todayIn(period.tz));

  return (
    <div ref={pickerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-pressed={custom}
        aria-expanded={open}
        aria-label={`${translate("analyticsCustomPeriod")}: ${dates}`}
        onClick={toggle}
        className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border whitespace-nowrap"
        style={control(custom)}
      >
        <Calendar className="w-4 h-4" aria-hidden="true" style={{ color: "var(--color-text-secondary)" }} />
        {dates}
      </button>
      {open && (
        // Por baixo do botao e por cima da pagina. O `relative` do involucro segura-o dentro do <main>.
        // Abre no mes do primeiro dia do periodo: num intervalo o react-datepicker volta a esse mes no
        // primeiro render seguinte, por isso um `openToDate` noutro mes saltava sozinho.
        <div ref={calendarRef} className={`absolute top-full mt-2 z-30 range-calendar ${alignRight ? "right-0" : "left-0"}`}>
          <DatePicker
            inline
            selectsRange
            startDate={pickedStart || toDate(period.from)}
            endDate={pickedStart ? null : toDate(period.to)}
            onChange={choose}
            minDate={toDate(MIN_DATE)}
            maxDate={toDate(lastDay)}
            locale={language === "pt" ? "pt" : undefined}
            chooseDayAriaLabelPrefix={translate("analyticsChooseDay")}
            disabledDayAriaLabelPrefix={translate("analyticsDayUnavailable")}
            previousMonthAriaLabel={translate("analyticsPreviousMonth")}
            nextMonthAriaLabel={translate("analyticsNextMonth")}
          />
        </div>
      )}
    </div>
  );
};

// A linha dos filtros, na barra fixa da pagina (spec do redesign do RPAA-286): o periodo (Hoje, 7, 30 ou 90
// dias, ou datas escolhidas no calendario), as datas com que se compara (ou, quando os dados comecam
// dentro do periodo anterior, que nao ha comparacao e porque), uma nota sobre o periodo e o menu
// Exportar, com o que cada papel pode descarregar. O `cut` e o do resumo do pedido atual: a hora da
// comparacao e a dele, nunca a do relogio do browser (D12 do plano do Hoje). Tambem e o `cut`, e nao o
// relogio, que diz se a comparacao vem cortada: com a pagina aberta a passar a meia-noite o periodo e o
// resumo ficam no dia em que se escolheram, cortados, e o relogio ja vai no seguinte. O relogio so decide o
// texto enquanto o resumo de um dia que acaba hoje carrega ("Comparado com ontem", sem hora).
const AnalyticsFilters = ({ chatId, range, onRange, period, dataSince, previousComplete, exportOptions, cut }) => {
  const { translate, language } = useLanguage();
  const previous = previousPeriod(period);
  const since = dataSince ? new Date(dataSince).toLocaleDateString(LOCALES[language] || language, { dateStyle: "medium" }) : null;
  const endsToday = period.to === todayIn(period.tz);
  const previousDates = { from: formatDay(previous.from, language), to: formatDay(previous.to, language) };

  let compared;
  if (period.from === period.to) {
    if (cut) compared = translate("analyticsComparedWithYesterdayUntil", { time: timeIn(cut.now, period.tz, language) });
    else if (endsToday) compared = translate("analyticsComparedWithYesterday");
    else compared = translate("analyticsComparedWithDay", { day: previousDates.from });
  } else if (cut) {
    compared = translate("analyticsComparedWithUntil", { ...previousDates, time: timeIn(cut.now, period.tz, language) });
  } else {
    compared = translate("analyticsComparedWithShort", previousDates);
  }

  // O recalculo de que a nota fala so existe quando o corte das conversas e o dele, e nao o de agora; com
  // o recalculo noutro dia (logo a seguir a meia-noite) a hora leva a data.
  const recalculationDay = cut && cut.conversations !== cut.now ? todayIn(period.tz, new Date(cut.conversations)) : null;
  const recalculationTime = recalculationDay && timeIn(cut.conversations, period.tz, language);
  const cutNote = !recalculationDay
    ? null
    : recalculationDay === todayIn(period.tz, new Date(cut.now))
      ? translate("analyticsCutNote", { time: recalculationTime })
      : translate("analyticsCutNoteOtherDay", { day: formatDay(recalculationDay, language), time: recalculationTime });
  const isDaysPreset = !range.custom && range.preset !== TODAY;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div role="group" aria-label={translate("analyticsPeriod")} className="flex flex-wrap gap-1">
        {PRESETS.map((preset) => {
          const pressed = !range.custom && range.preset === preset;
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={pressed}
              onClick={() => onRange({ preset, custom: null })}
              className="px-3 py-1.5 text-sm rounded-lg border whitespace-nowrap"
              style={control(pressed)}
            >
              {preset === TODAY ? translate("analyticsToday") : translate("analyticsLastDays", { count: preset })}
            </button>
          );
        })}
        <CustomPeriod period={period} custom={Boolean(range.custom)} onRange={onRange} />
      </div>
      <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
        {previousComplete ? compared : translate("analyticsNoComparisonBefore", { date: since })}
      </span>
      <InfoTip
        label={translate("analyticsAboutPeriod")}
        text={[
          translate("analyticsTimeZoneNote", { tz: period.tz }),
          ...(isDaysPreset ? [translate("analyticsPresetEndsYesterday")] : []),
          ...(cutNote ? [cutNote] : []),
          ...(since ? [translate("analyticsDataSince", { date: since })] : []),
        ].join(" ")}
      />
      {exportOptions.length > 0 && <ExportMenu chatId={chatId} period={period} options={exportOptions} />}
    </div>
  );
};

export default AnalyticsFilters;
